/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Spin } from '@coze-arch/coze-design';

import {
  EMBED_MESSAGE_CHANNEL,
  EMBED_MESSAGE_VERSION,
  embedTargetOrigin,
  parseEmbedBridgeEvent,
  type EmbedNavigateCommand,
} from './embed-protocol';
import styles from './opencode-frame.module.less';

export interface OpenCodeFrameProps {
  embedUrl: string;
  opencodeSessionID: string;
  title?: string;
  className?: string;
  onSessionReady?: (sessionID: string) => void;
  onSessionError?: (sessionID: string, error: string) => void;
  onTitleSuggested?: (sessionID: string, title: string) => void;
}

/** OpenCode Web UI iframe（经 Studio /opencode/ 代理） */
export const OpenCodeFrame: React.FC<OpenCodeFrameProps> = ({
  embedUrl,
  opencodeSessionID,
  title = 'OpenCode',
  className,
  onSessionReady,
  onSessionError,
  onTitleSuggested,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initialUrlRef = useRef(embedUrl);
  const targetOriginRef = useRef(embedTargetOrigin(embedUrl));
  const latestTargetRef = useRef({ embedUrl, sessionID: opencodeSessionID });
  const latestRequestRef = useRef<{ requestId: string; sessionID: string }>();
  const readyRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const onSessionReadyRef = useRef(onSessionReady);
  const onSessionErrorRef = useRef(onSessionError);
  const onTitleSuggestedRef = useRef(onTitleSuggested);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    onSessionReadyRef.current = onSessionReady;
    onSessionErrorRef.current = onSessionError;
    onTitleSuggestedRef.current = onTitleSuggested;
  }, [onSessionError, onSessionReady, onTitleSuggested]);

  const sendLatestTarget = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    const targetOrigin = targetOriginRef.current;
    if (!readyRef.current || !iframeWindow || !targetOrigin) {
      return;
    }
    const target = latestTargetRef.current;
    if (embedTargetOrigin(target.embedUrl) !== targetOrigin) {
      setLoading(false);
      onSessionErrorRef.current?.(
        target.sessionID,
        'OpenCode embed origin changed unexpectedly',
      );
      return;
    }
    const requestId = `studio-${++requestSequenceRef.current}`;
    latestRequestRef.current = { requestId, sessionID: target.sessionID };
    const command: EmbedNavigateCommand = {
      channel: EMBED_MESSAGE_CHANNEL,
      version: EMBED_MESSAGE_VERSION,
      type: 'navigate',
      requestId,
      sessionId: target.sessionID,
    };
    iframeWindow.postMessage(command, targetOrigin);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }
    const onLoad = () => {
      setLoading(true);
    };
    const onMessage = (event: MessageEvent) => {
      if (
        event.source !== iframe.contentWindow ||
        event.origin !== targetOriginRef.current
      ) {
        return;
      }
      const message = parseEmbedBridgeEvent(event.data);
      if (!message) {
        return;
      }
      if (message.type === 'ready') {
        readyRef.current = true;
        sendLatestTarget();
        return;
      }
      if (message.type === 'title-suggested') {
        onTitleSuggestedRef.current?.(message.sessionId, message.title);
        return;
      }
      const latest = latestRequestRef.current;
      if (
        !latest ||
        message.requestId !== latest.requestId ||
        message.sessionId !== latest.sessionID
      ) {
        return;
      }
      if (message.type === 'session-ready') {
        setLoading(false);
        onSessionReadyRef.current?.(message.sessionId);
        return;
      }
      setLoading(false);
      onSessionErrorRef.current?.(message.sessionId, message.error);
    };
    iframe.addEventListener('load', onLoad);
    window.addEventListener('message', onMessage);
    iframe.src = initialUrlRef.current;
    return () => {
      iframe.removeEventListener('load', onLoad);
      window.removeEventListener('message', onMessage);
    };
  }, [sendLatestTarget]);

  useEffect(() => {
    latestTargetRef.current = { embedUrl, sessionID: opencodeSessionID };
    setLoading(true);
    sendLatestTarget();
  }, [embedUrl, opencodeSessionID, sendLatestTarget]);

  return (
    <div className={classNames(styles.root, className)}>
      <iframe
        ref={iframeRef}
        className={styles.iframe}
        title={title}
        allow="clipboard-read; clipboard-write"
      />
      {loading ? (
        <div className={styles['loading-mask']}>
          <Spin spinning />
        </div>
      ) : null}
    </div>
  );
};
